// WebSocketService — manages connection lifecycle to a `moment serve` instance.
//
// Handles: connection, reconnection with exponential backoff, stale detection,
// keepalive (pong responses), and close-code-aware behavior per FACET-ADR-019.

import type { WsServerMessage } from '../types/websocket.ts';
import { WS_CLOSE_NORMAL, WS_CLOSE_MAX_CLIENTS } from '../types/websocket.ts';

// ---------------------------------------------------------------------------
// Backoff constants
// ---------------------------------------------------------------------------

const BACKOFF_STEPS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const STALE_TIMEOUT_MS = 45_000;

// ---------------------------------------------------------------------------
// Callbacks
// ---------------------------------------------------------------------------

export interface WebSocketCallbacks {
  onMessage: (msg: WsServerMessage) => void;
  onStatusChange: (status: 'connecting' | 'connected' | 'disconnected' | 'error') => void;
  onError: (message: string) => void;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export class WebSocketService {
  private ws: WebSocket | null = null;
  private callbacks: WebSocketCallbacks | null = null;
  private host = 'localhost';
  private port = 4321;
  private secure = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private staleTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalDisconnect = false;
  private noReconnectOnClose = false;
  private staleDisconnect = false;

  // ── Public API ──────────────────────────────────────────────────────────

  connect(host: string, port: number, secure: boolean, callbacks: WebSocketCallbacks): void {
    // Silent teardown — don't emit 'disconnected' when transitioning to a new connection
    this.teardown();
    this.host = host;
    this.port = port;
    this.secure = secure;
    this.callbacks = callbacks;
    this.intentionalDisconnect = false;
    this.noReconnectOnClose = false;
    this.staleDisconnect = false;
    this.reconnectAttempt = 0;
    this.openConnection();
  }

  disconnect(): void {
    this.teardown();
    this.callbacks?.onStatusChange('disconnected');
  }

  private teardown(): void {
    this.intentionalDisconnect = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // ── Connection lifecycle ────────────────────────────────────────────────

  private openConnection(): void {
    this.callbacks?.onStatusChange('connecting');

    const protocol = this.secure ? 'wss' : 'ws';
    const url = `${protocol}://${this.host}:${this.port}`;

    try {
      this.ws = new WebSocket(url);
    } catch (error) {
      this.ws = null;
      this.noReconnectOnClose = true;
      this.callbacks?.onStatusChange('error');
      this.callbacks?.onError(
        error instanceof Error
          ? `Invalid WebSocket URL: ${error.message}`
          : 'Invalid WebSocket URL',
      );
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.callbacks?.onStatusChange('connected');
      this.resetStaleTimer();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      this.resetStaleTimer();

      // Respond to server pings (keepalive)
      if (event.data === 'ping') {
        this.ws?.send('pong');
        return;
      }

      try {
        const msg: WsServerMessage = JSON.parse(event.data as string);
        this.callbacks?.onMessage(msg);
      } catch {
        // Ignore malformed messages
      }
    };

    this.ws.onerror = () => {
      // The close event fires immediately after — handle reconnection there.
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.clearStaleTimer();

      if (this.intentionalDisconnect) return;

      // Stale-timeout close triggers code 1000 from the browser, but it's
      // NOT a clean server shutdown — treat it as an unexpected disconnect.
      if (this.staleDisconnect) {
        this.staleDisconnect = false;
        this.scheduleReconnect();
        return;
      }

      if (event.code === WS_CLOSE_NORMAL) {
        // Intentional server shutdown — do NOT auto-reconnect
        this.noReconnectOnClose = true;
        this.callbacks?.onStatusChange('disconnected');
        this.callbacks?.onError('Moment server shut down');
        return;
      }

      if (event.code === WS_CLOSE_MAX_CLIENTS) {
        this.noReconnectOnClose = true;
        this.callbacks?.onStatusChange('error');
        this.callbacks?.onError('Maximum clients reached — cannot connect');
        return;
      }

      // WS_CLOSE_GOING_AWAY or unexpected disconnect → reconnect
      this.scheduleReconnect();
    };
  }

  // ── Reconnection with exponential backoff ───────────────────────────────

  private scheduleReconnect(): void {
    if (this.intentionalDisconnect || this.noReconnectOnClose) return;

    const delay = BACKOFF_STEPS[Math.min(this.reconnectAttempt, BACKOFF_STEPS.length - 1)];
    this.reconnectAttempt++;

    this.callbacks?.onStatusChange('connecting');

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.intentionalDisconnect && !this.noReconnectOnClose) {
        this.openConnection();
      }
    }, delay);
  }

  // ── Stale detection ─────────────────────────────────────────────────────

  private resetStaleTimer(): void {
    this.clearStaleTimer();
    this.staleTimer = setTimeout(() => {
      // No message (including ping) received for >45 seconds — assume dead.
      // Set the staleDisconnect flag so the onclose handler knows this is NOT
      // a clean server shutdown (browsers fire close code 1000 for client-initiated close).
      if (this.ws) {
        this.staleDisconnect = true;
        this.ws.close();
      }
    }, STALE_TIMEOUT_MS);
  }

  private clearStaleTimer(): void {
    if (this.staleTimer) {
      clearTimeout(this.staleTimer);
      this.staleTimer = null;
    }
  }

  // ── Timer cleanup ───────────────────────────────────────────────────────

  private clearTimers(): void {
    this.clearStaleTimer();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

// Singleton instance
export const webSocketService = new WebSocketService();
